import { getUserContainer } from '../../container/user-module'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from '../../modules/users'
import { Box, Modal, Tooltip } from '@mui/material'
import { AppTable, Field } from '../../components/table'
import { fromModel, User, UserDTO, UserQuery } from '../../modules/users/models/User'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'
import { BoolQueryParam, Query, QueryParam, QueryParamN } from '../../common/api/Query'
import { Actions, Pager, Search, SearchValue } from '../../components/table/types'
import genericStyle from '../../common/utils/generic.module.css'
import iconView from '../../assets/user-table/view.svg'
import iconDelete from '../../assets/user-table/delete.svg'
import iconChat from '../../assets/user-table/chat.svg'
import calendarIcon from '../../assets/user-table/calenar.svg'
import addPatientIcon from '../../assets/user-table/addPatient.svg'
import searchIcon from '../../assets/user-table/searchIcon.svg'

import {
  ROUTE_CALENDAR,
  ROUTE_CALENDAR_EXERCISES,
  ROUTE_DASHBOARD,
  ROUTE_MESSENGER,
  ROUTE_PATIENTS,
  ROUTE_STATISTICS,
  ROUTE_USERS_CREATE,
} from '../../routes/routes-constants'
import { navigate } from '@reach/router'
import { UserGender } from '../../modules/users/enums/UserGender'
import editIcon from '../../assets/user-table/edit.svg'
import { LoggedUserService } from '../../modules/users/services/LoggedUserService'
import { CustomModal } from '../../components/modal/CustomModal'
import { useSnackbar } from 'notistack'
import { useIsOpenMenuLeftContext } from '../../common/utils/isOpenMenuLeftContext'
import { Pagination } from '../../components/table/Pagination'
import { Searcher } from '../../components/table/Searcher'

const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)

export function Table() {
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [users, setUsers] = useState<User[]>([])
  const [count, setCount] = useState<number>(0)
  const [doctors, setDoctors] = useState<User[]>([])
  const [page, setPage] = useState<number>(0)
  const [pager, setPager] = useState<Pager>()
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
  const [currentUser, setCurrentUser] = useState<User>()
  const { openMenuLeft } = useIsOpenMenuLeftContext()
  const { enqueueSnackbar } = useSnackbar()
  const innerWidth = window.innerWidth
  const [loggedUserData, setLoggedUserData] = useState<User | null>(null)

  const [searcher, setSearcher] = useState<SearchValue<UserQuery>[]>([
    {
      name: 'firstName',
      label: t('search') + '...',
    },
  ])

  useEffect(() => {
    const loggedUser = loggedUserService.get()
    if (loggedUser?.id) {
      userService.getByID(loggedUser.id).subscribe((userData) => {
        if (userData) {
          setLoggedUserData(userData)
        }
      })
    }
  }, [])

  useEffect(() => {
    if (!loggedUserData) {
      return
    }

    setIsLoading(true)

    const queryParams = [
      {
        name: 'isPatient',
        value: new BoolQueryParam(true),
      },
      {
        name: 'assignedID',
        value: loggedUserService.get()?.id || '',
      },
      ...searcherQuery(searcher),
    ]

    userService
      .getFilteredList(
        new Query({
          query: queryParams,
          pager: { offset: page * rowsPerPage, limit: rowsPerPage },
        })
      )
      .subscribe((res) => {
        setUsers(res.items)
        setCount(res.count)
        setIsLoading(false)
      })
  }, [loggedUserData, searcher, page, rowsPerPage])

  useEffect(() => {
    if (!users.length) {
      return
    }

    const ids = users.map((u) => u.assignedID)
    userService
      .getFilteredList(
        new Query({
          pager: { offset: 0, limit: ids.length },
          query: [
            {
              name: 'ids',
              value: ids,
            },
          ],
        })
      )
      .subscribe((res) => {
        setDoctors(res.items)
      })
  }, [users])

  useEffect(() => {
    setPager({
      page,
      count,
      handleChangePage,
      rowsPerPage,
      handleChangeRowsPerPage,
    })
  }, [page, count, rowsPerPage])

  const handleChangePage = (event: unknown, value: number) => setPage(value)

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (Number.isNaN(event.target.value)) {
      setRowsPerPage(10)
      return
    }
    setRowsPerPage(Number.parseInt(event.target.value))
    setPage(0)
  }

  const removeUser = (u: User) => {
    setCurrentUser(u)
    setOpenDeleteModal(true)
  }

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
  }

  const handleDeleteUser = () => {
    if (!currentUser) return
    const userUnassigned = new User(currentUser)
    userUnassigned.assignedID = ''
    userService.update(fromModel(userUnassigned)).subscribe((res) => {
      setIsLoading(true)
      setOpenDeleteModal(false)
      enqueueSnackbar(t('userUnassigned'), { variant: 'success' })
    })
  }

  const handlerAddUser = () => navigate(ROUTE_USERS_CREATE)

  const fields: Field<User>[] = [
    {
      name: 'firstName',
      label: t('fullName'),
      renderFunc: (f, i) => i.firstName + ' ' + i.lastName,
    },
    {
      name: 'historyRecordNumber',
      label: t('historyRecordNumber'),
    },
    {
      name: 'diagnosisSummary',
      label: t('diagnosisSummary'),
    },
    {
      name: 'assignedID',
      label: t('assignedID'),
      renderFunc: (f, i) => {
        if (!i.assignedID) {
          return ''
        }

        const d = doctors.find((d) => d.id === i.assignedID)
        if (!d?.lastName) {
          return ''
        }
        return t(d.gender === UserGender.Female ? 'femaleDoctor' : 'maleDoctor') + ' ' + d.lastName
      },
    },
  ]

  const search: Search<UserQuery> = {
    searchValues: searcher,
    handleSearch: (svs: SearchValue<UserQuery>[]) => {
      setSearcher(svs)
      setPage(0)
    },
  }

  const editUser = (u: User) => navigate(`${ROUTE_USERS_CREATE}/${u.id}`)

  const seeCharts = (u: User) => navigate(`${ROUTE_STATISTICS}/${u.id}`).then()
  const seeDashboard = (u: User) => navigate(`${ROUTE_DASHBOARD}/${u.id}`)

  const deleteUser = (u: User) => {
    setCurrentUser(u)
    setOpenDeleteModal(true)
    // navigate(ROUTE_PATIENTS)
  }

  const calendarUser = (u: User) => navigate(`${ROUTE_CALENDAR_EXERCISES}/${u.id}`)

  const isCreator = (u: User): boolean => u.assignedID === loggedUserService.get()?.id

  const actions: Actions<User> = {
    actionsColumn: t('actions'),
    items: [
      {
        handler: calendarUser,
        icon: calendarIcon,
        label: 'calendar',
        hidden: (u) => !isCreator(u),
      },
      {
        handler: seeDashboard,
        icon: iconView,
        label: 'see',
        hidden: (u) => !isCreator(u),
      },
      {
        handler: () => navigate(ROUTE_MESSENGER),
        icon: iconChat,
        label: 'chat',
        hidden: (u) => !isCreator(u),
      },
      {
        handler: editUser,
        icon: editIcon,
        label: 'edit',
        hidden: (u) => !isCreator(u),
      },
      {
        handler: deleteUser,
        icon: iconDelete,
        label: 'delete',
        hidden: (u) => !isCreator(u),
      },
    ],
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        marginLeft: innerWidth > 900 ? (openMenuLeft ? '50px' : '') : '',
      }}
    >
      <Box
        style={{
          maxWidth:
            innerWidth > 900 ? (openMenuLeft ? 'calc(100% - 250px)' : 'calc(100% - 300px)') : '95%',
          position: 'relative',
        }}
        className={genericStyle.pageContainer}
      >
        <div className={genericStyle.container}>
          <Modal open={openDeleteModal} onClose={handleCloseDeleteModal}>
            <CustomModal
              title={t('unassignPatient')}
              warningText={t('irreversibleUnassignPatientAction')}
              handleClose={handleCloseDeleteModal}
              handleSave={handleDeleteUser}
            />
          </Modal>
          <AppTable
            rowKeyField="id"
            items={users}
            fields={fields}
            actions={actions}
            search={search}
            isLoading={isLoading}
          />
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            width={'100%'}
          >
            <Box sx={{ marginLeft: openMenuLeft ? '35px' : '10px' }}>
              {pager && <Pagination {...pager} />}
            </Box>
            <Box display={'flex'} alignItems={'center'} mt={openMenuLeft ? 0 : 2} mr={10}>
              <img
                style={{ marginRight: 2, marginBottom: 2 }}
                src={searchIcon}
                alt={'search patient'}
              />
              <Searcher search={search} />
            </Box>
          </Box>
        </div>
        <Box
          style={{ position: 'absolute', right: -30, bottom: -3, cursor: 'pointer' }}
          onClick={handlerAddUser}
        >
          <Tooltip title={t('addPatient')}>
            <img src={addPatientIcon} alt="add patient" width={90} />
          </Tooltip>
        </Box>
      </Box>
    </div>
  )
}

const searcherQuery = (
  svs: SearchValue<UserQuery>[]
): QueryParam<UserQuery>[] | QueryParamN<UserQuery>[] =>
  svs.filter((sv) => sv.value).map((sv) => ({ name: sv.name, value: sv.value as string }))
